import type { FileRejection } from 'react-dropzone';
import {
  MAX_FILE_SIZE,
  MAX_PDF_FILE_SIZE,
  PDF_MIME_TYPE,
} from '@/modules/vip/Steps/components/VipTransfer/components/TransferRequestCard/components/VipTransferFormCard/constants';

export const VIP_FILE_ERROR = {
  invalidFileType: 'invalidFileType',
  maxSize: 'maxSize',
  pdfMaxSize: 'pdfMaxSize',
} as const;

export type VipFileErrorCode =
  (typeof VIP_FILE_ERROR)[keyof typeof VIP_FILE_ERROR];

export interface IVipFileError {
  code: VipFileErrorCode;
  file: File;
}

const ERROR_I18N_KEYS: Record<VipFileErrorCode, string> = {
  [VIP_FILE_ERROR.invalidFileType]:
    'steps.vipTransfer.transferForm.invalidFileType',
  [VIP_FILE_ERROR.maxSize]: 'steps.vipTransfer.transferForm.errorMaxSize',
  [VIP_FILE_ERROR.pdfMaxSize]: 'steps.vipTransfer.transferForm.errorPdfMaxSize',
};

const isPdfTooLarge = (file: File): boolean =>
  file.type === PDF_MIME_TYPE && file.size > MAX_PDF_FILE_SIZE;

const isFileTooLarge = (file: File): boolean => file.size > MAX_FILE_SIZE;

export const getFileErrorCode = (file: File): VipFileErrorCode | null => {
  if (isPdfTooLarge(file)) return VIP_FILE_ERROR.pdfMaxSize;
  if (isFileTooLarge(file)) return VIP_FILE_ERROR.maxSize;
  return null;
};

export const getRejectionErrorCode = (
  rejection?: FileRejection
): VipFileErrorCode => {
  if (rejection?.errors[0]?.code === 'file-invalid-type') {
    return VIP_FILE_ERROR.invalidFileType;
  }

  return (
    (rejection && getFileErrorCode(rejection.file)) ?? VIP_FILE_ERROR.maxSize
  );
};

export const getFileErrorMessageKey = (code: VipFileErrorCode): string =>
  ERROR_I18N_KEYS[code];

export const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};
